/**
 * Shared keyboard-shortcut guards. Previously duplicated inline in app-sidebar
 * and GlobalSearchDialog; centralized here so every global/scoped hotkey handler
 * agrees on "is the user typing" and "is a modal open" (the two checks that keep
 * bare-key shortcuts from firing at the wrong time).
 */

import { useCallback } from 'react';

/** True when the event target is a text input the user is typing into. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA') return true;
  return target.getAttribute('contenteditable') === 'true';
}

/** True when a Radix dialog/modal is open — bare-key shortcuts should yield to it. */
export function anotherDialogIsOpen(): boolean {
  return !!document.querySelector('[role="dialog"][data-state="open"]');
}

/**
 * Wraps a Dialog/Sheet's `onEscapeKeyDown` so a field with
 * `data-escape-revert="true"` can revert-and-stay-focused instead of the
 * dialog closing. Radix's own Escape-to-dismiss listener runs on `document`
 * in the capture phase — always before the focused field's own bubble-phase
 * handler — so a field can never win that race by calling
 * `preventDefault()` itself; this has to run from inside the dialog
 * primitive's own `onEscapeKeyDown`, which Radix invokes before checking
 * `event.defaultPrevented`. Composes with (doesn't replace) the caller's own
 * handler, matching dialog.tsx's `usePopperAwarePointerDownOutside`.
 *
 * Lives here, not in dialog.tsx, because dialog.tsx already imports
 * Sheet/SheetContent from sheet.tsx — sheet.tsx importing this back from
 * dialog.tsx would be circular.
 */
export function useEscapeRevertGuard<T extends (event: KeyboardEvent) => void>(
  onEscapeKeyDown?: T,
): (event: KeyboardEvent) => void {
  return useCallback(
    (event: KeyboardEvent) => {
      const active = document.activeElement;
      if (active instanceof HTMLElement && active.dataset.escapeRevert === 'true') {
        event.preventDefault();
      }
      onEscapeKeyDown?.(event);
    },
    [onEscapeKeyDown],
  );
}

/** A triage action a single keypress maps to in the Engagement Hub. */
export type TriageKeyAction = 'next' | 'prev' | 'reply' | 'edit' | 'flag' | 'hide';

/**
 * Map a bare keypress to an Engagement Hub triage action (j/k move, r reply,
 * e edit, f flag, x hide), or null if the key isn't bound. Pure and case-
 * insensitive so the hotkey wiring stays unit-testable without a DOM. Callers
 * MUST still gate on isTypingTarget/anotherDialogIsOpen before consulting this.
 */
export function resolveTriageKey(key: string): TriageKeyAction | null {
  switch (key.toLowerCase()) {
    case 'j':
    case 'arrowdown':
      return 'next';
    case 'k':
    case 'arrowup':
      return 'prev';
    case 'r':
      return 'reply';
    case 'e':
      return 'edit';
    case 'f':
      return 'flag';
    case 'x':
      return 'hide';
    default:
      return null;
  }
}
